/**
 * SOLID Review
 * S — Strong. One concern: the legal documents and the account's legal acts as data — the documents, the
 *     consent status, the consents to record, the GDPR requests, and the gate's decisions' shapes.
 * O — Good. A new legal act is one request and one op.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Only the legal moves.
 * D — Good. zod only.
 */
import { z } from 'zod';

/**
 * THE LEGAL SIDE (frontend strict-ports plan, FE4a, 2026-10-04). The legal modal's tabs, the GDPR
 * sections and the re-acceptance gate called the legal REST adapter from React; the gate also decided,
 * inline, whether a signed-in player must re-accept. These are the shapes that flow now.
 *
 * A document's `type` is a string, not the four-value enum: documents are display data the server owns,
 * and a fifth kind must show up as a document rather than fail the whole list. The rules that care
 * (Terms, Privacy) compare against their two literals.
 */
const PayloadLegalDocument = z.object({
  type: z.string(),
  version: z.string(),
  title: z.string(),
  content: z.string(),
}).meta({ id: 'PayloadLegalDocument' });

export const PayloadLegalDocuments = z.object({ documents: z.array(PayloadLegalDocument) }).meta({ id: 'PayloadLegalDocuments' });

/** Whether the account's recorded consents match the current Terms and Privacy versions. */
export const PayloadConsentStatus = z.object({
  hasTerms: z.boolean(),
  hasPrivacy: z.boolean(),
  allCurrent: z.boolean(),
}).meta({ id: 'PayloadConsentStatus' });

export const RequestRecordConsent = z.object({ documentType: z.string(), documentVersion: z.string() }).meta({ id: 'RequestRecordConsent' });

/** A GDPR subject-access export: whatever the player holds — the server declares no schema for it. */
export const PayloadDataExport = z.object({ data: z.unknown() }).meta({ id: 'PayloadDataExport' });

export const CommandDeleteAccount = z.object({ code: z.string() }).meta({ id: 'CommandDeleteAccount' });

// ── the gate's decisions ──────────────────────────────────────────────────────────────────────

export const CommandPlanLegalReview = z.object({ status: PayloadConsentStatus }).meta({ id: 'CommandPlanLegalReview' });
export const DtoPlanLegalReview = z.object({ fetchDocuments: z.boolean() }).meta({ id: 'DtoPlanLegalReview' });
export const CommandLegalReviewOf = z.object({ documents: z.array(PayloadLegalDocument) }).meta({ id: 'CommandLegalReviewOf' });
/** The documents to re-accept, Terms then Privacy — empty when there is nothing to ask. */
export const DtoLegalReview = z.object({ review: z.array(PayloadLegalDocument) }).meta({ id: 'DtoLegalReview' });
export const CommandLegalAcceptance = z.object({ review: z.array(PayloadLegalDocument) }).meta({ id: 'CommandLegalAcceptance' });
export const DtoLegalAcceptance = z.object({ consents: z.array(RequestRecordConsent) }).meta({ id: 'DtoLegalAcceptance' });

export type IPayloadLegalDocument = z.infer<typeof PayloadLegalDocument>;
export type IPayloadLegalDocuments = z.infer<typeof PayloadLegalDocuments>;
export type IPayloadRecordConsent = z.infer<typeof RequestRecordConsent>;
export type IPayloadDataExport = z.infer<typeof PayloadDataExport>;
export type ICommandDeleteAccount = z.infer<typeof CommandDeleteAccount>;
export type ICommandPlanLegalReview = z.infer<typeof CommandPlanLegalReview>;
export type IEntityPlanLegalReview = z.infer<typeof DtoPlanLegalReview>;
export type ICommandLegalReviewOf = z.infer<typeof CommandLegalReviewOf>;
export type IEntityLegalReview = z.infer<typeof DtoLegalReview>;
export type ICommandLegalAcceptance = z.infer<typeof CommandLegalAcceptance>;
export type IEntityLegalAcceptance = z.infer<typeof DtoLegalAcceptance>;
